import { z } from "zod";
import { defineStepConfig } from "../../registry/helpers";
import { entityOfType } from "../../utils/system-entities";
import { SystemEntity, DocumentType } from "@erp/shared-types";

/**
 * Document type enum for classification config
 */
const documentTypeEnum = z.enum(DocumentType);

/**
 * Classify Document Step
 *
 * Classifies document attachments from an inbound email and stores
 * classification results in the Document model.
 *
 * Uses Extend AI classification processor to analyze each document
 * and categorize it based on the configured document types.
 *
 * The OTHER classification is always included automatically as a fallback.
 *
 * Classification results are stored in Document.classificationType,
 * allowing downstream steps to query classified documents directly from the database.
 */
export const classifyDocumentStep = defineStepConfig({
  type: "classify_document",
  name: "Classify Document",
  description: "Classify documents and store results in database",
  category: "Transformation",

  inputSchema: z.object({
    inboundEmail: entityOfType(SystemEntity.INBOUND_EMAIL).describe(
      "The inbound email whose documents should be classified",
    ),
  }),

  outputSchema: z.object({
    inboundEmail: entityOfType(SystemEntity.INBOUND_EMAIL).describe(
      "Pass-through of the inbound email for downstream steps",
    ),
  }),

  configSchema: z.object({
    documentTypes: z
      .array(documentTypeEnum)
      .optional()
      .describe(
        "Array of DocumentType values to classify documents into. OTHER is always included as fallback. Defaults to [PURCHASE_ORDER] for backward compatibility.",
      ),
  }),

  emits: [] as const,

  execution: {
    startToCloseTimeout: "3 minutes",
    retry: {
      maximumAttempts: 1,
    },
    nonRetryableErrorTypes: [
      "InvalidFileFormatError",
      "UnsupportedMimeTypeError",
    ],
  },
});
